import React, { useCallback, useMemo, useEffect } from "react";
import { Image, Form, Input } from "semantic-ui-react";
import { useFormik } from "formik";
import { useTranslation } from "react-i18next";
import { initialValues, validationSchema } from "./InstitutionForm.form";
import { InstitutionApi } from "../../../../api";
import { useAuth } from "../../../../hooks";
import { image } from "../../../../assets";
import { useDropzone } from "react-dropzone";

const institutionController = new InstitutionApi();

export function InstitutionForm(props) {
  const { close, onReload, user, institution, plans } = props;
  const { accessToken } = useAuth();
  const { t } = useTranslation("institutions");

  // Generar opciones para el dropdown de planes
  const planOptions = useMemo(() => {
    if (!plans) return [];
    return plans.map((plan) => ({
      key: plan._id,
      value: plan._id,
      text: plan.title,
      numberStudents: plan.studentsNumber,
      numberTeachers: plan.teacherNumber,
    }));
  }, [plans]);

  const formik = useFormik({
    initialValues: initialValues(user, institution, !institution),
    validationSchema: validationSchema(institution, t),
    validateOnChange: false,
    onSubmit: async (values) => {
      try {
        if (!institution) {
          await institutionController.createInstitution(accessToken, values);
        } else {
          if (formik.values.fileAvatar && institution.avatar) {
            await institutionController.deleteAvatar(
              accessToken,
              institution.avatar,
            );
          }
          await institutionController.updateInstitution(
            accessToken,
            institution._id,
            values,
          );
        }
        onReload();
        close();
      } catch (error) {
        console.error(error);
      }
    },
  });

  // CORREGIDO: Desestructuramos las propiedades y funciones que necesitamos de formik.
  // `values` para leer los datos y `setFieldValue` para modificarlos.
  const { values, setFieldValue } = formik;

  // Dropzone para avatar
  const onDrop = useCallback(
    (files) => {
      const file = files[0];
      if (file && ["image/jpeg", "image/png"].includes(file.type)) {
        setFieldValue("avatar", URL.createObjectURL(file));
        setFieldValue("fileAvatar", file);
      }
    },
    [setFieldValue], // <-- CORREGIDO: Ahora depende de la función estable 'setFieldValue'.
  );
  const { getRootProps, getInputProps } = useDropzone({
    accept: { "image/jpeg": [".jpeg", ".jpg"], "image/png": [".png"] },
    onDrop,
    multiple: false,
  });

  // Al seleccionar plan en creación, actualizamos licencias
  useEffect(() => {
    if (!institution && values.planEducaId) {
      const sel = planOptions.find((o) => o.value === values.planEducaId);
      if (sel) {
        setFieldValue("studentsnum", sel.numberStudents ?? "");
        setFieldValue("teachersnum", sel.numberTeachers ?? "");
      }
    }
    // CORREGIDO: Ahora usamos `values` y `setFieldValue` directamente.
  }, [values.planEducaId, planOptions, institution, setFieldValue]);

  const getAvatar = () => {
    if (values.fileAvatar) return values.avatar;
    return values.avatar || image.noAvatar;
  };

  return (
    <Form onSubmit={formik.handleSubmit} className="institution-form">
      {/* Avatar */}
      <Form.Field>
        <label>{t("form.avatarLabel")}</label>
        <div {...getRootProps()} className="institution-form__avatar">
          <input {...getInputProps()} />
          <Image avatar size="small" src={getAvatar()} />
        </div>
      </Form.Field>

      {/* Partner ID */}
      <Form.Field>
        <label>{t("form.partnerIdLabel")}</label>
        <span>{formik.values.partnerId}</span>
      </Form.Field>

      {/* Nombre e info básica */}
      <Form.Field
        control={Form.Input}
        label={t("form.nameLabel")}
        name="institutionnam"
        placeholder={t("form.namePlaceholder")}
        onChange={formik.handleChange}
        value={formik.values.institutionnam}
        error={formik.errors.institutionnam}
      />
      <Form.Field
        control={Form.Input}
        label={t("form.addressLabel")}
        name="address"
        placeholder={t("form.addressPlaceholder")}
        onChange={formik.handleChange}
        value={formik.values.address}
        error={formik.errors.address}
      />

      <Form.Group widths="equal">
        <Form.Field
          control={Form.Input}
          label={t("form.cityLabel")}
          name="city"
          placeholder={t("form.cityPlaceholder")}
          onChange={formik.handleChange}
          value={formik.values.city}
          error={formik.errors.city}
        />
        <Form.Field
          control={Form.Input}
          label={t("form.countryLabel")}
          name="country"
          placeholder={t("form.countryPlaceholder")}
          onChange={formik.handleChange}
          value={formik.values.country}
          error={formik.errors.country}
        />
      </Form.Group>

      <Form.Field
        control={Form.Input}
        label={t("form.websiteLabel")}
        name="website"
        placeholder={t("form.websitePlaceholder")}
        onChange={formik.handleChange}
        value={formik.values.website}
        error={formik.errors.website}
      />

      <Form.Group widths="equal">
        <Form.Field
          control={Form.Input}
          label={t("form.phoneLabel")}
          name="phone"
          placeholder={t("form.phonePlaceholder")}
          onChange={formik.handleChange}
          value={formik.values.phone}
          error={formik.errors.phone}
        />

        {/* Plan contratado: editable solo en creación */}
        {institution ? (
          <Form.Field>
            <label>{t("form.planLabel")}</label>
            <Input readOnly value={institution.planEduca?.title || "-"} />
          </Form.Field>
        ) : (
          <Form.Field
            control={Form.Dropdown}
            label={t("form.planLabel")}
            placeholder={t("form.planPlaceholder")}
            options={planOptions}
            selection
            onChange={(_, d) => formik.setFieldValue("planEducaId", d.value)}
            value={formik.values.planEducaId}
            error={formik.errors.planEducaId}
          />
        )}
      </Form.Group>

      {/* Contacto */}
      <Form.Group widths="equal">
        <Form.Field
          control={Form.Input}
          label={t("form.contactNameLabel")}
          name="firstname"
          placeholder={t("form.contactNamePlaceholder")}
          onChange={formik.handleChange}
          value={formik.values.firstname}
          error={formik.errors.firstname}
        />
        <Form.Field
          control={Form.Input}
          label={t("form.lastNameLabel")}
          name="lastname"
          placeholder={t("form.lastNamePlaceholder")}
          onChange={formik.handleChange}
          value={formik.values.lastname}
          error={formik.errors.lastname}
        />
      </Form.Group>

      {/* Licencias: solo lectura */}
      <Form.Group widths="equal">
        <Form.Field
          control={Form.Input}
          label={t("form.studentLicensesLabel")}
          name="studentsnum"
          value={formik.values.studentsnum}
          disabled
          error={formik.errors.studentsnum}
        />
        <Form.Field
          control={Form.Input}
          label={t("form.teacherLicensesLabel")}
          name="teachersnum"
          value={formik.values.teachersnum}
          disabled
          error={formik.errors.teachersnum}
        />
      </Form.Group>

      {/* Email & Password */}
      <Form.Field
        control={Form.Input}
        label={t("form.emailLabel")}
        name="email"
        placeholder={t("form.emailPlaceholder")}
        onChange={formik.handleChange}
        value={formik.values.email}
        error={formik.errors.email}
        autoComplete="off"
      />
      <Form.Field
        control={Form.Input}
        type="password"
        label={t("form.passwordLabel")}
        name="password"
        placeholder={institution ? t("form.passwordPlaceholderExisting") : t("form.passwordPlaceholderNew")}
        onChange={formik.handleChange}
        value={formik.values.password}
        error={formik.errors.password}
        autoComplete="new-password"
      />

      <Form.Button primary fluid loading={formik.isSubmitting} type="submit">
        {institution ? t("form.updateButton") : t("form.createButton")}
      </Form.Button>
    </Form>
  );
}
